import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { getLocationDisplayName } from "@/lib/location-utils";
import { useTenant } from "@/hooks/useTenant";


export interface AtlasDriver {
  id: string;
  name: string;
  certification: string | null;
  shift: string | null;
  home_base_id: string | null;
  order_ids: string[] | null;
  isDayOff: boolean;
}

export interface AtlasTractor {
  id: string;
  name: string;
  base_truck: string | null;
  capabilities: string[];
  home_base_id: string | null;
}

export interface AtlasTrailer {
  id: string;
  trailer_id: string;
  scac: string | null;
  checked_in_at: string | null;
  has_bol: boolean;
  truck_id: string | null;
}

export interface AtlasLocation {
  id: string;
  name: string;
  short_code: string | null;
  city: string | null;
  province_state: string | null;
  lat: number;
  lng: number;
  drivers: AtlasDriver[];
  tractors: AtlasTractor[];
  tractorCount: number;
  trailers: AtlasTrailer[];
  trailerCount: number;
  track_in_yms: boolean;
}

export function useAtlasData(excludeTractorNames: string[] = []) {
  const today = format(new Date(), "yyyy-MM-dd");
  const { tenantId } = useTenant();

  const { data: locations, isLoading: locationsLoading } = useQuery({
    queryKey: ["atlas-locations"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("locations")
        .select("*, customer:customers(*)")
        .eq("archived", false)
        .order("short_code");
      if (error) throw error;
      return data;
    },
  });

  const { data: drivers, isLoading: driversLoading } = useQuery({
    queryKey: ["atlas-drivers", today],
    queryFn: async () => {
      const { data, error } = await supabase.from("drivers").select("*");
      if (error) throw error;
      return data;
    },
  });

  const { data: daysOff, isLoading: daysOffLoading } = useQuery({
    queryKey: ["atlas-days-off", today],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("driver_days_off")
        .select("driver_id")
        .eq("date", today);
      if (error) throw error;
      return new Set(data.map((d) => d.driver_id));
    },
  });

  const { data: tractors, isLoading: tractorsLoading } = useQuery({
    queryKey: ["atlas-tractors"],
    queryFn: async () => {
      const { data, error } = await supabase.from("tractors").select("*");
      if (error) throw error;
      return data;
    },
  });

  // Fetch tractor_timeline entries where tractors are currently at a location
  const { data: tractorTimeline, isLoading: timelineLoading } = useQuery({
    queryKey: ["atlas-tractor-timeline-on-site"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("tractor_timeline")
        .select("tractor_id, location_id")
        .eq("status", "at_location")
        .is("end_time", null);
      if (error) throw error;
      return data;
    },
  });

  const { data: trailers, isLoading: trailersLoading } = useQuery({
    queryKey: ["atlas-trailers", tenantId],
    enabled: !!tenantId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("trailers")
        .select("id, trailer_id, scac, current_location_id")
        .eq("current_location_status", "at_site")
        .eq("tenant_id", tenantId!);
      if (error) throw error;
      return data;
    },
  });

  // Fetch latest checked_in event per trailer for arrival time
  const { data: trailerCheckins, isLoading: checkinsLoading } = useQuery({
    queryKey: ["atlas-trailer-checkins"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("trailer_events")
        .select("trailer_id, occurred_at, truck_id")
        .eq("event_type", "checked_in")
        .order("occurred_at", { ascending: false });
      if (error) throw error;
      // Build map: trailer_id -> { occurred_at, truck_id }
      const map = new Map<string, { occurred_at: string; truck_id: string | null }>();
      for (const row of data) {
        if (!map.has(row.trailer_id)) map.set(row.trailer_id, { occurred_at: row.occurred_at, truck_id: row.truck_id });
      }
      return map;
    },
  });

  // Fetch trailer_event_documents to know which trailers have BOL attached
  const { data: trailerBolSet, isLoading: bolLoading } = useQuery({
    queryKey: ["atlas-trailer-bols"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("trailer_event_documents")
        .select("trailer_event_id, document_type")
        .eq("document_type", "bol");
      if (error) throw error;
      // Get the event IDs that have bols
      const eventIds = data.map((d) => d.trailer_event_id);
      if (eventIds.length === 0) return new Set<string>();
      // Now get the trailer_ids from those events
      const { data: events, error: evErr } = await supabase
        .from("trailer_events")
        .select("trailer_id")
        .in("id", eventIds);
      if (evErr) throw evErr;
      return new Set((events || []).map((e) => e.trailer_id));
    },
  });

  const isLoading = locationsLoading || driversLoading || daysOffLoading || tractorsLoading || trailersLoading || timelineLoading || checkinsLoading || bolLoading;

  // Build a map of tractor_id -> location_id from active timeline entries
  const tractorLocationMap = useMemo(() => {
    const map = new Map<string, string>();
    (tractorTimeline || []).forEach((entry) => {
      if (entry.location_id) {
        map.set(entry.tractor_id, entry.location_id);
      }
    });
    return map;
  }, [tractorTimeline]);

  // Build set of excluded tractor names (case-insensitive) for deduplication
  const excludedNamesLower = useMemo(
    () => new Set(excludeTractorNames.map((n) => n.toLowerCase())),
    [excludeTractorNames]
  );

  const atlasLocations: AtlasLocation[] = useMemo(() => {
    return (locations || [])
      .filter((loc) => loc.latitude != null && loc.longitude != null)
      .map((loc) => {
        const locDrivers: AtlasDriver[] = (drivers || [])
          .filter((d) => d.home_base_id === loc.id)
          .map((d) => ({
            id: d.id,
            name: d.name,
            certification: d.certification,
            shift: d.shift,
            home_base_id: d.home_base_id,
            order_ids: d.order_ids,
            isDayOff: daysOff?.has(d.id) ?? false,
          }));

        // Use tractor_timeline to determine which tractors are at this location,
        // excluding any that are currently in the Samsara in-transit feed
        const locTractors: AtlasTractor[] = (tractors || [])
          .filter((t) => {
            const timelineLocationId = tractorLocationMap.get(t.id);
            if (timelineLocationId !== loc.id) return false;
            // Exclude tractors currently showing in the In Transit tab
            if (excludedNamesLower.has(t.name.toLowerCase())) return false;
            return true;
          })
          .map((t) => ({
            id: t.id,
            name: t.name,
            base_truck: t.base_truck,
            capabilities: t.capabilities || [],
            home_base_id: t.home_base_id,
          }));

        const locTrailers: AtlasTrailer[] = (trailers || [])
          .filter((t) => t.current_location_id === loc.id)
          .map((t) => {
            const checkin = trailerCheckins?.get(t.id);
            return {
              id: t.id,
              trailer_id: t.trailer_id,
              scac: t.scac,
              checked_in_at: checkin?.occurred_at ?? null,
              has_bol: trailerBolSet?.has(t.id) ?? false,
              truck_id: checkin?.truck_id ?? null,
            };
          });

        return {
          id: loc.id,
          name: getLocationDisplayName(loc),
          short_code: loc.short_code ?? null,
          city: loc.city,
          province_state: loc.province_state,
          lat: loc.latitude!,
          lng: loc.longitude!,
          drivers: locDrivers,
          tractors: locTractors,
          tractorCount: locTractors.length,
          trailers: locTrailers,
          trailerCount: locTrailers.length,
          track_in_yms: loc.track_in_yms,
        } satisfies AtlasLocation;
      });
  }, [locations, drivers, daysOff, tractors, trailers, tractorLocationMap, excludedNamesLower, trailerCheckins, trailerBolSet]);

  // Summary counts
  const allDrivers = atlasLocations.flatMap((l) => l.drivers);
  const summary = {
    totalDrivers: allDrivers.length,
    available: allDrivers.filter((d) => !d.isDayOff).length,
    dayOff: allDrivers.filter((d) => d.isDayOff).length,
    totalLocations: atlasLocations.length,
    totalTractors: atlasLocations.reduce((sum, l) => sum + l.tractorCount, 0),
    totalTrailers: atlasLocations.reduce((sum, l) => sum + l.trailerCount, 0),
  };

  return { atlasLocations, summary, isLoading };
}
